/** Shared VYSOR wizard definitions (TurboChat widget + TurboChat page). */

import { canRunVysorAction } from './rolePermissions';

export type WizardFieldType = 'text' | 'choice' | 'application' | 'instance' | 'idp-platform';

export type InstanceFilter = 'all' | 'pending-sso';

export interface WizardField {
  key: string;
  question: string;
  required: boolean;
  type?: WizardFieldType;
  options?: string[];
  hint?: string;
  instanceFilter?: InstanceFilter;
}

export type WizardHandoff = 'navigate' | 'chat';

/** Matches backend quick-action RBAC (`buildRoleQuickActions` / `canRunVysorAction`). */
export function canRunVysorWizard(role: string | undefined, flowId: WizardFlow['id']): boolean {
  return canRunVysorAction(role, flowId);
}

export interface WizardFlow {
  id: 'setup-app' | 'iga-entitlements' | 'configure-sso';
  title: string;
  intro: string;
  fields: WizardField[];
  storageKey: string;
  navigateTo: string;
  /** navigate = open target page/dialog after wizard; chat = stay in VYSOR and message the assistant */
  handoff: WizardHandoff;
}

export const SETUP_APP_FLOW: WizardFlow = {
  id: 'setup-app',
  title: 'application',
  intro:
    "Let's register a new application. I'll ask a few essentials — you can answer in your own words anytime.",
  storageKey: '',
  navigateTo: '',
  handoff: 'chat',
  fields: [
    { key: 'name', required: true, question: "What's the application's display name? (e.g. \"Salesforce Production\")" },
    {
      key: 'application_id',
      required: false,
      question: 'Give it a short identifier (lowercase slug, e.g. "salesforce-prod").',
      hint: 'Usually auto-derived from the display name.',
    },
    {
      key: 'business_function',
      required: true,
      type: 'choice',
      question: 'Which business function does it serve?',
      options: ['HR', 'Finance', 'Engineering', 'IT', 'Sales', 'Marketing', 'Operations', 'Other'],
      hint: 'Inferred from the name when obvious (e.g. HR Portal → HR).',
    },
    { key: 'description', required: false, question: 'Add a short description (or choose Skip).' },
    {
      key: 'risk_level',
      required: false,
      type: 'choice',
      question: "What's the risk level?",
      options: ['Not Defined', 'Low', 'Medium', 'High', 'Critical'],
      hint: 'Defaults to Not Defined.',
    },
    {
      key: 'status',
      required: false,
      type: 'choice',
      question: 'Initial status?',
      options: ['Active', 'Inactive'],
      hint: 'Defaults to Active.',
    },
  ],
};

export const ENTITLEMENT_FLOW: WizardFlow = {
  id: 'iga-entitlements',
  title: 'entitlement',
  intro:
    "Let's create an entitlement. Pick the application and environment, then I'll collect the details and create it for you.",
  storageKey: '',
  navigateTo: '',
  handoff: 'chat',
  fields: [
    {
      key: 'application_id',
      required: true,
      type: 'application',
      question: 'Which application is this entitlement for?',
      hint: 'All applications in your catalog are listed below — tap one, or type the exact app name.',
    },
    {
      key: 'instance_id',
      required: true,
      type: 'instance',
      instanceFilter: 'all',
      question: 'Which environment / instance?',
    },
    { key: 'name', required: true, question: 'Entitlement name? (e.g. "Read-Only Reports")' },
    { key: 'description', required: true, question: 'Short description of what this entitlement grants.' },
    {
      key: 'entitlement_type',
      required: false,
      type: 'choice',
      question: 'What type of entitlement?',
      options: ['Role', 'Permission', 'Group', 'Access', 'Resource'],
      hint: 'Defaults to Role.',
    },
  ],
};

export const ADD_INSTANCE_SUBFLOW_FIELDS: WizardField[] = [
  {
    key: 'new_instance_name',
    required: true,
    question: 'What should we call this environment? (e.g. "Production")',
  },
  {
    key: 'new_instance_environment',
    required: true,
    type: 'choice',
    question: 'Which environment type is this?',
    options: ['Production', 'Staging', 'Development', 'Test', 'UAT', 'Testing'],
  },
  {
    key: 'new_instance_description',
    required: false,
    question: 'Add a short description (or choose Skip).',
  },
];

export const SSO_CONFIGURE_FLOW: WizardFlow = {
  id: 'configure-sso',
  title: 'SSO configuration',
  intro:
    "I'll guide you through SSO setup in chat. Pick the application, environment, and protocol — your company IdP (Okta/Azure) is detected automatically — then I'll submit the configuration for review.",
  storageKey: '',
  navigateTo: '',
  handoff: 'chat',
  fields: [
    {
      key: 'application_id',
      required: true,
      type: 'application',
      question: 'Which application should we configure SSO for?',
      hint: 'All applications in your catalog are listed below — tap one, or type the exact app name.',
    },
    {
      key: 'instance_id',
      required: true,
      type: 'instance',
      instanceFilter: 'pending-sso',
      question: 'Which environment / instance needs SSO?',
      hint: 'Instances that still need SSO are shown first.',
    },
    {
      key: 'sso_type',
      required: true,
      type: 'choice',
      question: 'Which SSO protocol does the app use?',
      options: ['SAML', 'OIDC', 'Not sure'],
    },
    {
      key: 'platform',
      required: false,
      type: 'idp-platform',
      question: 'Which identity provider platform?',
      hint: 'Usually auto-detected from your tenant Settings (Okta/Azure). Only asked if you need to override.',
    },
    {
      key: 'metadata_or_notes',
      required: false,
      question:
        'Paste a **SAML metadata URL/XML**, **OIDC discovery URL**, redirect URIs, or any vendor guide notes (or choose Skip).',
    },
  ],
};

export const WIZOR_FLOW_BY_ID: Record<WizardFlow['id'], WizardFlow> = {
  'setup-app': SETUP_APP_FLOW,
  'iga-entitlements': ENTITLEMENT_FLOW,
  'configure-sso': SSO_CONFIGURE_FLOW,
};

/** Build the assistant message after the SSO wizard completes. */
export function buildSsoWizardChatMessage(collected: Record<string, string>): string {
  const appName = collected.application_name || collected.application_hint || 'not specified';
  const instanceName = collected.instance_name || 'not specified';
  const lines = [
    'I want to configure SSO for an application.',
    `Application: ${appName}`,
    `Environment / instance: ${instanceName}`,
  ];
  if (collected.application_id) {
    lines.push(`[ID: ${collected.application_id}, instanceId: ${collected.instance_id || 'pending'}]`);
  }
  lines.push(`SSO type: ${collected.sso_type || 'not specified'}`);
  lines.push(`IdP platform: ${collected.platform || 'not specified'}`);
  if (collected.metadata_or_notes?.trim()) {
    lines.push(`Additional info: ${collected.metadata_or_notes.trim()}`);
  }
  lines.push('Please guide me step by step and ask one question at a time before opening any form.');
  return lines.join('\n');
}

/** Map wizard collected values to SSO prefill when app + instance are known. */
export function buildSsoPrefillFromWizard(collected: Record<string, string>) {
  if (!collected.application_id || !collected.instance_id) return null;

  const platformId = collected.platform_id || collected.platform || '';
  const legacyMap: Record<string, string> = {
    Okta: 'okta',
    'Azure AD': 'azure',
    Google: 'google',
    Other: 'generic_saml',
  };
  const platform = legacyMap[platformId] || platformId || 'generic_saml';

  const sso_type: 'SAML' | 'OIDC' | undefined =
    collected.sso_type === 'OIDC'
      ? 'OIDC'
      : collected.sso_type === 'SAML'
        ? 'SAML'
        : platform === 'generic_oidc'
          ? 'OIDC'
          : platform === 'generic_saml' || platform === 'okta' || platform === 'azure' || platform === 'google'
            ? 'SAML'
            : undefined;

  const metadataNotes = collected.metadata_or_notes?.trim() || '';
  const metadataFromFetch = collected.metadata_content?.trim();
  const redirectUris = (collected.redirect_uris || '')
    .split(/[\n,]+/)
    .map(u => u.trim())
    .filter(u => /^https:\/\//i.test(u));

  return {
    applicationId: collected.application_id,
    applicationName: collected.application_name,
    instanceId: collected.instance_id,
    sso_type,
    platform,
    metadata_url:
      (metadataNotes.startsWith('http') ? metadataNotes : collected.metadata_url)
      || collected.oidc_discovery_endpoint,
    entity_id: collected.metadata_entity_id,
    acs_url: collected.metadata_acs_url,
    certificate:
      collected.metadata_certificate
      || (collected.metadata_has_certificate === 'true' ? '(from metadata)' : undefined),
    oidc_discovery_endpoint: collected.oidc_discovery_endpoint || undefined,
    redirect_uris: redirectUris.length ? redirectUris : undefined,
    scopes: ['openid', 'profile', 'email'],
    grant_type: 'authorization_code_pkce' as const,
    client_id: collected.client_id?.trim() || undefined,
    business_justification: metadataNotes.startsWith('http') ? undefined : metadataNotes || undefined,
    metadata_content: metadataFromFetch,
  };
}

/** Enrich navigate-handoff sessionStorage payloads with resolved catalog labels. */
export function buildWizardStoragePayload(flowId: WizardFlow['id'], collected: Record<string, string>): Record<string, string> {
  if (flowId === 'iga-entitlements') {
    return {
      name: collected.name || '',
      description: collected.description || '',
      entitlement_type: collected.entitlement_type || '',
      environment: collected.instance_name || '',
      application_id: collected.application_id || '',
      instance_id: collected.instance_id || '',
    };
  }
  return { ...collected };
}
